"use client";

import { c as _c } from "react/compiler-runtime";
import Image, { ImageProps } from "next/image";
import { AspectRatio } from "../ui/aspect-ratio";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Heading } from "../ui/heading";
import { Button } from "../ui/button";
import { Clock, Download, Sparkles, Users } from "lucide-react";
import { RenderIf } from "./render-if";
import { useParams } from "next/navigation";
import Link from "next/link";
import { Badge } from "../ui/badge";
import { CoursePrice } from "./course-price";
import { usePopup } from "@/providers/popup-provider";
type CourseCardProps = {
  name: string;
  image: string;
  imageProps?: Omit<ImageProps, "src" | "alt">;
  tag?: string;
  totalEnroll?: string;
  duration?: number | string;
  slug?: string;
  accreditation_name?: string;
  icon?: string;
  serviceId?: number;
  modeOfTraining?: string;
  sellingPriceNumber?: number;
  currency?: string;
  serviceFlags?: string[];
  isPriceHidden?: boolean;
  isGenericPageOnly?: boolean;
};
export const CourseCard = t0 => {
  const $ = _c(87);
  const {
    name,
    image,
    imageProps,
    icon,
    accreditation_name,
    modeOfTraining,
    duration,
    totalEnroll,
    sellingPriceNumber,
    currency,
    slug,
    tag,
    serviceId,
    serviceFlags: t1,
    isPriceHidden: isPriceHiddenProp,
    isGenericPageOnly
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const serviceFlags = t2;
  const {
    country
  } = useParams();
  const {
    openPopup
  } = usePopup();
  let t3;
  if ($[2] !== isPriceHiddenProp || $[3] !== serviceFlags) {
    t3 = isPriceHiddenProp ?? (serviceFlags?.includes("HIDE_PRICE_ON_WEBSITE") || false);
    $[2] = isPriceHiddenProp;
    $[3] = serviceFlags;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isPriceHidden = t3;
  const courseHref = country && !isGenericPageOnly ? `/${country}/course/${slug}` : `/course/${slug}`;
  const t4 = Boolean(tag);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Sparkles size={10} className="text-amber-400" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== tag) {
    t6 = <Badge className="bg-primary text-primary-foreground absolute top-0 left-0 z-10 rounded-l-none">{t5}{tag}</Badge>;
    $[6] = tag;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <RenderIf condition={t4}>{t6}</RenderIf>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = Boolean(image);
  let t9;
  if ($[11] !== image || $[12] !== imageProps || $[13] !== name) {
    t9 = <Image src={image} alt={name} fill={true} sizes="360px" className="object-cover transition-transform duration-500 ease-out group-hover:scale-105" {...imageProps} />;
    $[11] = image;
    $[12] = imageProps;
    $[13] = name;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <RenderIf condition={t8}>{t9}</RenderIf>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7) {
    t11 = <AspectRatio ratio={2.25} className="bg-muted w-full overflow-hidden rounded-xl">{t7}{t10}</AspectRatio>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== icon) {
    t12 = Boolean(icon) && typeof icon === "string" && icon.startsWith("https://");
    $[21] = icon;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = icon || "";
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      width: "auto",
      height: "auto"
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13) {
    t15 = <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-100 bg-slate-50 p-1"><Image src={t13} quality={75} alt="provider icon" width={24} height={24} className="h-auto w-auto object-contain" style={t14} /></div>;
    $[24] = t13;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t15) {
    t16 = <RenderIf condition={t12}>{t15}</RenderIf>;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = Boolean(accreditation_name);
  let t18;
  if ($[29] !== accreditation_name) {
    t18 = <span className="text-primary text-[10px] leading-none font-black tracking-widest uppercase">{accreditation_name}</span>;
    $[29] = accreditation_name;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t17 || $[32] !== t18) {
    t19 = <RenderIf condition={t17}>{t18}</RenderIf>;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t16 || $[35] !== t19) {
    t20 = <div className="my-3 flex items-center gap-2.5">{t16}{t19}</div>;
    $[34] = t16;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div />;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== name) {
    t22 = <Heading as="h6" render={t21} className="mb-3">{name}</Heading>;
    $[38] = name;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  const t23 = Boolean(modeOfTraining);
  let t24;
  if ($[40] !== modeOfTraining) {
    t24 = <Badge variant="outline" className="mb-4">{modeOfTraining}</Badge>;
    $[40] = modeOfTraining;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t23 || $[43] !== t24) {
    t25 = <RenderIf condition={t23}>{t24}</RenderIf>;
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Clock size={13} className="text-muted-foreground" />;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const t27 = duration ? `${duration}` : "-";
  let t28;
  if ($[46] !== t27) {
    t28 = <div className="flex items-center gap-1.5">{t26}<span>{t27}</span></div>;
    $[46] = t27;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="text-slate-300">•</span>;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Users size={13} className="text-muted-foreground" />;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== totalEnroll) {
    t31 = totalEnroll ? totalEnroll.toLocaleString() : "-";
    $[50] = totalEnroll;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== t31) {
    t32 = <div className="flex items-center gap-1.5">{t30}<span>{t31} Enrolled</span></div>;
    $[52] = t31;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== t28 || $[55] !== t32) {
    t33 = <div className="text-muted-foreground mb-5 flex items-center gap-3 text-xs font-semibold">{t28}{t29}{t32}</div>;
    $[54] = t28;
    $[55] = t32;
    $[56] = t33;
  } else {
    t33 = $[56];
  }
  let t34;
  if ($[57] !== openPopup || $[58] !== serviceId) {
    t34 = () => openPopup({
      type: "enquiry",
      title: "Enquire About Course & Pricing",
      description: "Get detailed information about course pricing, upcoming batch dates, and training schedules.",
      data: {
        serviceId,
        submitLabel: "Enquire Now"
      }
    });
    $[57] = openPopup;
    $[58] = serviceId;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== currency || $[61] !== isPriceHidden || $[62] !== sellingPriceNumber || $[63] !== serviceFlags || $[64] !== t34) {
    t35 = <CoursePrice price={sellingPriceNumber} currency={currency} isPriceHidden={isPriceHidden} serviceFlags={serviceFlags} onEnquire={t34} />;
    $[60] = currency;
    $[61] = isPriceHidden;
    $[62] = sellingPriceNumber;
    $[63] = serviceFlags;
    $[64] = t34;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== t11 || $[67] !== t20 || $[68] !== t22 || $[69] !== t25 || $[70] !== t33 || $[71] !== t35) {
    t36 = <CardContent>{t11}{t20}{t22}{t25}{t33}{t35}</CardContent>;
    $[66] = t11;
    $[67] = t20;
    $[68] = t22;
    $[69] = t25;
    $[70] = t33;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  const t37 = courseHref ?? "#";
  let t38;
  if ($[73] !== t37) {
    t38 = <Button variant="outline" size="default" nativeButton={false} render={<Link href={t37} />}>View Certification</Button>;
    $[73] = t37;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  let t39;
  if ($[75] !== openPopup || $[76] !== serviceId) {
    t39 = () => openPopup({
      type: "download-brochure",
      title: "Download Curriculum",
      data: {
        serviceId
      }
    });
    $[75] = openPopup;
    $[76] = serviceId;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Download />;
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] !== t39) {
    t41 = <Button size="default" onClick={t39}>{t40} Curriculum</Button>;
    $[79] = t39;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== t38 || $[82] !== t41) {
    t42 = <CardFooter className="bg-card justify-between gap-3">{t38}{t41}</CardFooter>;
    $[81] = t38;
    $[82] = t41;
    $[83] = t42;
  } else {
    t42 = $[83];
  }
  let t43;
  if ($[84] !== t36 || $[85] !== t42) {
    t43 = <div className="group p-1"><Card className="transition-all duration-300 hover:-translate-y-1">{t36}{t42}</Card></div>;
    $[84] = t36;
    $[85] = t42;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  return t43;
};